/**
 * Formato numérico del tablero de indicadores.
 *
 * <b>Por qué es un módulo y no un `toLocaleString` en cada widget.</b> Hoy cada
 * widget formatea a su manera: el KPI concatena prefijo y sufijo, los ejes de los
 * gráficos llaman a `toLocaleString('es-CO')` por su cuenta y la tabla no formatea
 * nada. El resultado es que la misma cifra se ve distinta en tres sitios de la
 * misma pantalla.
 *
 * Las reglas de aquí salen de la especificación del tablero:
 *
 *  - Se abrevia por magnitud (K, M, B), pero <b>nunca</b> en tablas ni en enteros
 *    de conteo: una tabla existe para comparar cifras exactas, y "20 pedidos" no
 *    se lee mejor como "20".
 *  - La moneda es un <b>símbolo separado y más pequeño</b>, no un prefijo pegado
 *    al número. Por eso `abbreviate` devuelve las piezas por separado en vez de
 *    una cadena: quien pinta decide el tamaño de cada una.
 *  - Todo número que se refresca se pinta con `tabular-nums`. Sin eso los dígitos
 *    cambian de ancho y la cifra tiembla en cada refresco.
 */

/**
 * Locale por defecto del producto.
 *
 * Se exporta porque es el ÚNICO punto donde vive esa decisión: cualquier `toLocaleString`,
 * `toLocaleDateString` o `localeCompare` del tablero lo usa en vez de cablear `'es-CO'` por su
 * cuenta — que es lo que hacían el subtítulo del contenedor, el badge de alto volumen y el
 * comparador de la tabla.
 */
export const DASHBOARD_LOCALE = 'es-CO';
const DEFAULT_LOCALE = DASHBOARD_LOCALE;

/**
 * Moneda del alcance activo.
 *
 * <b>Hoy el símbolo está cableado, y es a propósito.</b> El endpoint de compañías
 * no devuelve la moneda local (`CompanyDto = {id, code, name}`), aunque el dato
 * exista en base (`segm_companies_prj.local_currency_id`). Mostrar un código de
 * moneda que nadie verificó sería peor que no mostrarlo.
 *
 * El día que el API la exponga, <b>este es el único sitio que cambia</b>: ningún
 * widget formatea moneda por su cuenta.
 */
export interface Currency {
  /** Símbolo visible: `$`, `US$`. */
  symbol: string;
  /** Código ISO, para el selector de compañía. `null` mientras no sea confiable. */
  iso: string | null;
  locale: string;
}

export const DEFAULT_CURRENCY: Currency = { symbol: '$', iso: null, locale: DEFAULT_LOCALE };

/** Lo mínimo que el tablero necesita saber de una compañía para resolver su moneda. */
export interface CurrencyScope {
  /** Moneda local de la compañía. Hoy ningún host la envía. */
  localCurrency?: { symbol?: string; iso?: string } | null;
}

/**
 * <b>El único punto donde se decide en qué moneda está una cifra.</b>
 *
 * Ningún widget formatea moneda por su cuenta: todos pasan por aquí. Eso es lo que
 * hace que el día que el API de compañías exponga `local_currency_id` haya que
 * cambiar exactamente una función — y no rastrear cada `toLocaleString` del módulo.
 *
 * Mientras tanto devuelve el peso con `iso: null`, que es una afirmación honesta:
 * "sé qué símbolo pintar, no sé de qué moneda es". Por eso el selector de compañía
 * no muestra código de moneda todavía — mostrar "COP" para todas sería afirmar algo
 * que nadie verificó.
 */
export function resolveCurrency(scope?: CurrencyScope): Currency {
  const local = scope?.localCurrency;
  if (local?.symbol) {
    return { symbol: local.symbol, iso: local.iso ?? null, locale: DEFAULT_LOCALE };
  }
  return DEFAULT_CURRENCY;
}

/** Las tres piezas de una cifra, para que quien pinta decida el tamaño de cada una. */
export interface AbbreviatedValue {
  /** Símbolo de moneda, o `null` si el indicador no es monetario. */
  symbol: string | null;
  /** El número ya formateado al locale. */
  number: string;
  /**
   * Sufijo de magnitud, o `null` si no se abrevió.
   *
   * Es una cadena libre y NO una unión `K | M | B` a propósito: lo pone `Intl` según
   * el locale, y las escalas no coinciden entre idiomas — en español 5.230.000.000
   * lleva `M` (escala larga) y en inglés `B`.
   */
  scale: string | null;
}

export interface AbbreviateOptions {
  /** Símbolo de moneda a devolver en `symbol`. */
  currency?: string | null;
  /** Entero de conteo: nunca se abrevia (pedidos, documentos, registros). */
  integer?: boolean;
  locale?: string;
  /**
   * Fuerza la abreviatura desde los miles, ignorando {@link ABBREVIATE_FROM}.
   *
   * <b>Es para los ejes de los gráficos y el centro de la dona</b>, donde el espacio
   * manda: un eje Y con `10.000.000` en cada marca se come el gráfico, y la cifra
   * exacta ya está en el tooltip. En un KPI o en una celda de tabla, no.
   */
  compact?: boolean;
}

const nf = (locale: string, decimals: number) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

/**
 * A partir de qué magnitud se abrevia una cifra.
 *
 * <b>Mil millones, y no un millón como decía el diseño original.</b> Dos razones,
 * y la segunda es de corrección, no de gusto:
 *
 *  1. <b>En pesos, los millones son cotidianos.</b> Diez millones de pesos son unos
 *     USD 2.500: una factura normal, no una cifra grande. Abreviar ahí le quita
 *     precisión a un número que el usuario lee exacto todo el día — y `500 K` en
 *     lugar de `500.000` no le ahorra nada a nadie. Peor aún con una TRM de 3.118,
 *     que abreviada a `3 K` deja de significar lo que significa.
 *  2. <b>La escala corta y la larga no coinciden.</b> En español un *billón* es
 *     10¹², no 10⁹, así que escribir "5,23 B" para cinco mil millones es
 *     sencillamente falso para quien lee en español — que es justo lo que hacía la
 *     versión anterior de esta función, con sus sufijos K/M/B escritos a mano.
 */
const ABBREVIATE_FROM = 1e9;

/**
 * Formateador compacto del locale.
 *
 * <b>La abreviatura la pone `Intl`, no nosotros.</b> Es lo que garantiza que el
 * sufijo sea el correcto en cada idioma: para 5.230.000.000, `es-CO` produce
 * `5230 M` —escala larga— y `en-US` produce `5.23B`. Codificar esa regla a mano es
 * exactamente el error que se está corrigiendo.
 */
const compactNf = (locale: string) =>
  new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 2 });

/** Separa el sufijo de magnitud del número, para poder pintarlos a tamaños distintos. */
function splitCompact(formatted: string): { number: string; scale: string | null } {
  // El compacto del locale viene como "5230 M", "10 M" o "3,12 K": el sufijo es lo
  // que quede después del último dígito.
  const match = formatted.match(/^([\d.,\s-]+?)\s*([^\d.,\s]+)$/);
  if (!match) return { number: formatted, scale: null };
  return { number: match[1].trim(), scale: match[2] };
}

/**
 * Parte una cifra en símbolo + número + magnitud.
 *
 * Por debajo de {@link ABBREVIATE_FROM} devuelve la cifra completa con sus
 * separadores de miles: en un ERP eso es lo que se espera leer.
 */
export function abbreviate(value: number, options: AbbreviateOptions = {}): AbbreviatedValue {
  const { currency = null, integer = false, locale = DEFAULT_LOCALE, compact = false } = options;

  if (!Number.isFinite(value)) {
    return { symbol: currency, number: '—', scale: null };
  }

  const abs = Math.abs(value);
  // Los conteos nunca se abrevian: "20 pedidos" no se lee mejor como "20".
  const shouldAbbreviate = !integer && (compact ? abs >= 1_000 : abs >= ABBREVIATE_FROM);

  if (!shouldAbbreviate) {
    return { symbol: currency, number: nf(locale, 0).format(Math.round(value)), scale: null };
  }

  const { number, scale } = splitCompact(compactNf(locale).format(value));
  return { symbol: currency, number, scale };
}

/**
 * La cifra abreviada como una sola cadena.
 *
 * Para donde no se puede componer con varios elementos: ejes de gráficos,
 * tooltips, el centro de una dona.
 */
export function formatCompact(value: number, options: AbbreviateOptions = {}): string {
  // `compact: true` va DESPUÉS del spread: es la razón de existir de esta función y no algo que
  // el llamador pueda apagar por accidente pasando `compact: undefined` en el objeto de opciones.
  const { symbol, number, scale } = abbreviate(value, { ...options, compact: true });
  return `${symbol ? symbol + ' ' : ''}${number}${scale ? ' ' + scale : ''}`;
}

/**
 * La cifra completa, sin abreviar.
 *
 * Es lo que va en el `title` del número y en las celdas de tabla: donde el
 * usuario compara magnitudes exactas, abreviar le quita justo lo que busca.
 */
export function formatExact(
  value: number,
  options: { currency?: string | null; decimals?: number; locale?: string } = {},
): string {
  const { currency = null, decimals = 0, locale = DEFAULT_LOCALE } = options;
  if (!Number.isFinite(value)) return '—';
  return `${currency ? currency + ' ' : ''}${nf(locale, decimals).format(value)}`;
}

/** Porcentaje con un decimal. El signo lo pone quien pinta, junto con el icono de tendencia. */
export function formatPercent(value: number, locale: string = DEFAULT_LOCALE): string {
  if (!Number.isFinite(value)) return '—';
  return `${nf(locale, 1).format(value)} %`;
}

/**
 * Fecha en formato de negocio: `19 ago 2026`.
 *
 * Nunca ISO crudo. Una fecha de máquina en una tabla que lee un gerente es un
 * detalle de implementación que se escapó a la pantalla.
 */
export function formatDate(value: string | Date, locale: string = DEFAULT_LOCALE): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  /**
   * <b>Se formatea en UTC, y no es un detalle.</b> Una fecha sin hora —`2026-08-19`,
   * que es como las manda el backend— se parsea como medianoche UTC; al pintarla en
   * la zona local (UTC-5 en Colombia) retrocede al día anterior, y la tabla mostraba
   * "18 de ago" para un dato del 19.
   *
   * Estas fechas son de negocio, no instantes: la fecha de una entrada de inventario
   * es la misma en Bogotá que en Madrid.
   */
  const hasTime = value instanceof Date
    || /[T ]\d{2}:/.test(String(value));

  return date
    .toLocaleDateString(locale, {
      day: 'numeric', month: 'short', year: 'numeric',
      ...(hasTime ? {} : { timeZone: 'UTC' }),
    })
    .replace(/\./g, '');
}

/**
 * Estilo obligatorio de todo número que se refresque en vivo.
 *
 * `tabular-nums` fija el ancho de los dígitos: sin él, un `1` ocupa menos que un `8` y la cifra
 * se desplaza en cada refresco.
 *
 * <b>El tracking de las cifras grandes del KPI NO va aquí</b> —aunque este comentario lo dijera—:
 * lo decide el contenedor con `--dash-kpi-tracking`, porque el mismo componente se pinta a tres
 * tamaños (compacto, ampliado y modo presentación) y el interletraje correcto depende del tamaño.
 */
export const NUMERIC_STYLE = {
  fontVariantNumeric: 'tabular-nums',
} as const;

/** @deprecated Usar {@link NUMERIC_STYLE}: son exactamente el mismo estilo. */
export const KPI_VALUE_STYLE = NUMERIC_STYLE;
